import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  WalletIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownLeftIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { walletService } from '../../services/walletService';
import type { WalletTransaction } from '../../types/wallet';
import MMQRImg from '../../assets/mmqr.svg';
import MMQRIcon from '../../assets/mmqr.png';
import APlusIcon from '../../assets/aplus-logo-40x40.png';
// import { useCountUp } from '../../hooks/useCountUp';
import { useCashInLogic } from '../../hooks/useCashInLogic';
import confetti from 'canvas-confetti';
import walletLogo from '../../assets/wallet/ez-wallet.png';
import { libraryCacheManager } from '../../utils/libraryCache';
import { openPaymentLink, isMobileOrPWA } from '../../utils/paymentUtils';
import { useRef } from 'react';

// Type definition for QRCodeStyling library
interface QRCodeStylingModule {
  default: new (options: QRCodeStylingOptions) => QRCodeStylingInstance;
}

interface QRCodeStylingOptions {
  width?: number;
  height?: number;
  type?: string;
  data?: string;
  image?: string;
  margin?: number;
  qrOptions?: {
    typeNumber?: number;
    mode?: string;
    errorCorrectionLevel?: string;
  };
  dotsOptions?: {
    color?: string;
    type?: string;
  };
  backgroundOptions?: {
    color?: string;
  };
  imageOptions?: {
    crossOrigin?: string;
    margin?: number;
    imageSize?: number;
  };
  cornersSquareOptions?: {
    color?: string;
    type?: string;
  };
  cornersDotOptions?: {
    color?: string;
    type?: string;
  };
}

interface QRCodeStylingInstance {
  append: (element: HTMLElement) => void;
  update: (options: Partial<QRCodeStylingOptions>) => void;
  _canvas?: HTMLCanvasElement;
}

interface CashInDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance?: number;
}

export default function CashInDialog({ isOpen, onClose, currentBalance = 0 }: CashInDialogProps) {
  const { user } = useAuth();
  console.log('[CashInDialog] Rendered with currentBalance prop:', currentBalance);
  console.log('[CashInDialog] User from context:', user);
  const {
    activeTab,
    setActiveTab,
    step,
    setStep,
    amount,
    setAmount,
    selectedPayment,
    showQR,
    error,
    setError,
    // withdrawAmount,
    // setWithdrawAmount,
    // withdrawPhone,
    // setWithdrawPhone,
    handleAmountChange,
    handleNextToPayment,
    handlePaymentSelect,
    isLoading,
    paymentQrCode,
    transactionId,
    pushNotiLink,
    setPushNotiLink,
  } = useCashInLogic(isOpen);

  const { refreshBalance } = useAuth();
  const qrCode = useRef<QRCodeStylingInstance | null>(null);

  useEffect(() => {
    if (isOpen) {
      refreshBalance();
    }
  }, [isOpen, refreshBalance]);

  // Handle QR Code Generation and Payment Links
  useEffect(() => {
    // MMQR Generation
    const generateQRCode = async () => {
      if (step === 'processing' && showQR && paymentQrCode && selectedPayment === 'MMQR') {
        let processedQrData = paymentQrCode;
        try {
          const utf8Bytes = new TextEncoder().encode(paymentQrCode);
          processedQrData = Array.from(utf8Bytes)
            .map((byte) => String.fromCharCode(byte))
            .join('');
        } catch (error) {
          console.warn('Myanmar text encoding error:', error);
        }

        try {
          const QRCodeStyling =
            (await libraryCacheManager.getQRCodeStylingLibrary()) as QRCodeStylingModule;

          if (!qrCode.current) {
            qrCode.current = new QRCodeStyling.default({
              width: 250,
              height: 250,
              type: 'svg',
              data: processedQrData,
              image: MMQRImg,
              margin: 0,
              qrOptions: {
                typeNumber: 0,
                mode: 'Byte',
                errorCorrectionLevel: 'M',
              },
              dotsOptions: {
                color: '#029797ff',
                type: 'rounded',
              },
              backgroundOptions: {
                color: '#ffffff',
              },
              imageOptions: {
                crossOrigin: 'anonymous',
                margin: 2,
                imageSize: 0.2,
              },
              cornersSquareOptions: {
                color: '#029797ff',
                type: 'extra-rounded',
              },
              cornersDotOptions: {
                color: '#029797ff',
                type: 'dot',
              },
            });

            setTimeout(() => {
              const el = document.getElementById('qr-code');
              if (el) {
                el.innerHTML = '';
                qrCode.current?.append(el);
              }
            }, 100);
          } else {
            qrCode.current.update({
              data: processedQrData,
            });
          }
        } catch (error) {
          console.error('QR generation error:', error);
          setError('Failed to generate QR code');
        }
      }
    };

    generateQRCode();

    return () => {
      if (!isOpen) {
        qrCode.current = null;
      }
    };
  }, [step, showQR, paymentQrCode, selectedPayment, isOpen, setError]);

  // Handle APlus Iframe logic
  const iframeLoadCount = useRef(0);

  // Reset load count when link changes
  useEffect(() => {
    if (pushNotiLink) {
      iframeLoadCount.current = 0;
    }
  }, [pushNotiLink]);

  // Listen for postMessage from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        if (
          event.data.includes('success') ||
          event.data.includes('close') ||
          event.data === 'Standard payment request executed'
        ) {
          setPushNotiLink(null);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setPushNotiLink]);

  const handleIframeLoad = () => {
    iframeLoadCount.current += 1;
    if (iframeLoadCount.current > 1) {
      setPushNotiLink(null);
    }
  };

  // Glue code for mobile deep link if needed (can be separate effect)
  useEffect(() => {
    if (
      step === 'processing' &&
      showQR &&
      paymentQrCode &&
      selectedPayment === 'APLUS' &&
      isMobileOrPWA
    ) {
      openPaymentLink(paymentQrCode);
    }
  }, [step, showQR, paymentQrCode, selectedPayment]);

  // Transaction History State
  const [history, setHistory] = useState<WalletTransaction[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedRole, setSelectedRole] = useState<'all' | 'user' | 'merchant'>('all');
  const LIMIT = 50;

  const fetchHistory = useCallback(
    async (offset: number) => {
      setIsLoadingHistory(true);
      try {
        const response = await walletService.getWalletTransactions(
          LIMIT,
          selectedRole === 'all' ? undefined : selectedRole,
        );
        if (response.success) {
          if (offset === 0) {
            setHistory(response.data.transactions);
          } else {
            setHistory((prev) => [...prev, ...response.data.transactions]);
          }
          setSkip(offset + LIMIT);
          // Check if we have more data
          setHasMore(offset + response.data.transactions.length < response.data.total);
        }
      } catch (err) {
        console.error('Failed to fetch transaction history:', err);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [selectedRole],
  );

  useEffect(() => {
    if (isOpen && activeTab === 'history') {
      // Reset and fetch when switching to history tab or opening dialog
      setSkip(0);
      setHasMore(false);
      fetchHistory(0);
    }
  }, [isOpen, activeTab, fetchHistory, selectedRole]);

  // Animations
  // const { value: animatedBalance } = useCountUp(currentBalance, { duration: 1500 });

  const confettiFiredRef = useRef(false);

  useEffect(() => {
    // Reset if we move away from success state (e.g. closing dialog or starting new payment)
    if (step !== 'success') {
      confettiFiredRef.current = false;
      return;
    }

    // Fire only if we are in success state and haven't fired yet for this session
    if (isOpen && step === 'success' && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      refreshBalance(); // Update balance immediately on success

      const count = 200;
      const defaults = {
        origin: { y: 0.7 },
        zIndex: 99999,
      };

      function fire(particleRatio: number, opts: confetti.Options) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        });
      }

      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });
      fire(0.2, {
        spread: 60,
      });
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
    }
  }, [isOpen, step, refreshBalance]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600 bg-green-50';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50';
      case 'FAILED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50'; // For unknown statuses
    }
  };

  const getIconForType = (type: string) => {
    if (['CREDIT', 'TOPUP', 'REFUND'].includes(type)) {
      return <ArrowDownLeftIcon className="w-5 h-5" />;
    }
    // Default to Up arrow for Outflow (DEBIT, PURCHASE, WITHDRAW, etc.)
    return <ArrowUpIcon className="w-5 h-5" />;
  };

  // Selected Transaction for Receipt View
  const [selectedTransaction, setSelectedTransaction] = useState<WalletTransaction | null>(null);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center sm:p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />

        {/* Dialog Container */}
        <div className="relative w-full md:max-w-md bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 md:zoom-in-95 flex flex-col h-[85vh] md:max-h-[700px] md:h-auto">
          {/* Mobile Handle Bar */}
          <div className="md:hidden absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-white/30 rounded-full z-30" />

          {/* Header Section with Gradient & Tabs */}
          <div
            className={`relative px-6 pt-6 pb-12 transition-all duration-500 flex-shrink-0
          ${step === 'success' ? 'bg-[#04BFBF]' : 'bg-gradient-to-br from-[#04BFBF] to-[#038C8C]'}`}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-white/50 hover:text-white rounded-full hover:bg-white/10 transition-colors z-20"
              aria-label="Close dialog"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Back Button (Logic for receipt view or payment steps) */}
            {(step === 'payment' && activeTab === 'top-up') || selectedTransaction ? (
              <button
                onClick={() => {
                  if (selectedTransaction) {
                    setSelectedTransaction(null);
                  } else {
                    setStep('amount');
                  }
                }}
                className="absolute top-4 left-4 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-white/50 hover:text-white rounded-full hover:bg-white/10 transition-colors z-20"
                aria-label="Go back"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
            ) : null}

            <div className="text-center relative z-10">
              {step === 'success' && activeTab === 'top-up' ? (
                <div className="flex flex-col items-center animate-in zoom-in duration-500 pb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 backdrop-blur-md">
                    <CheckCircleIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Success!</h3>
                  <p className="text-white/80 text-sm">Transaction Completed</p>
                </div>
              ) : selectedTransaction ? (
                <div className="pb-4 animate-in zoom-in duration-300">
                  <h3 className="text-xl font-bold text-white mb-2">Transaction Details</h3>
                  <p className="text-white/80 text-sm">Receipt</p>
                </div>
              ) : (
                <div className="pb-4">
                  <h3 className="text-xl font-bold text-white mb-2">EZ Wallet</h3>
                  <div className="flex items-center justify-center gap-2 text-white/90 text-sm bg-white/15 w-fit mx-auto px-4 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                    <WalletIcon className="w-4 h-4" />
                    <span className="font-medium font-mono tabular-nums">
                      {(
                        user?.balances?.MMK?.available ??
                        user?.walletBalance ??
                        currentBalance
                      ).toLocaleString()}{' '}
                      MMK
                    </span>
                  </div>
                </div>
              )}

              {/* Tabs - Hide if in success state or receipt view */}
              {!(step === 'success' && activeTab === 'top-up') && !selectedTransaction && (
                <div className="flex p-1 bg-black/20 rounded-xl w-full max-w-[320px] mx-auto backdrop-blur-lg mt-2">
                  <button
                    onClick={() => setActiveTab('top-up')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                        ${activeTab === 'top-up' ? 'bg-white text-[#04BFBF] shadow-sm' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                  >
                    Top Up
                  </button>
                  {/* <button
                  onClick={() => setActiveTab('withdraw')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                        ${activeTab === 'withdraw' ? 'bg-white text-[#04BFBF] shadow-sm' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                >
                  Withdraw
                </button> */}
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                        ${activeTab === 'history' ? 'bg-white text-[#04BFBF] shadow-sm' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                  >
                    History
                  </button>
                </div>
              )}
            </div>

            {/* Decorative circles */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-full -translate-x-10 -translate-y-10 blur-xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#04BFBF]/20 rounded-full translate-x-10 translate-y-10 blur-2xl pointer-events-none" />
          </div>

          {/* Content Section */}
          <div className="flex-1 rounded-t-[1.5rem] -mt-6 overflow-y-auto bg-gray-50 relative z-10 w-full [&::-webkit-scrollbar]:hidden">
            {/* Receipt View Content */}
            {selectedTransaction ? (
              <div className="min-h-full bg-gray-50 p-6 flex flex-col animate-in slide-in-from-right-10 duration-300">
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6 relative">
                  {/* Watermark Pattern */}
                  <div className="absolute inset-0 z-0 opacity-[0.06] pointer-events-none overflow-hidden select-none">
                    <div className="absolute -inset-1/2 flex flex-wrap content-center items-center justify-center gap-12 -rotate-[15deg] scale-125">
                      {Array.from({ length: 20 }).map((_, i) => (
                        <img key={i} src={walletLogo} alt="" className="w-24 h-24 object-contain" />
                      ))}
                    </div>
                  </div>

                  <div className="relative z-10">
                    <div className="flex flex-col items-center mb-6 border-b border-gray-100 pb-6">
                      <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          ['CREDIT', 'TOPUP', 'REFUND'].includes(selectedTransaction.type)
                            ? 'bg-[#04BFBF]/10 text-[#04BFBF]'
                            : 'bg-red-50 text-red-500'
                        }`}
                      >
                        {['CREDIT', 'TOPUP', 'REFUND'].includes(selectedTransaction.type) ? (
                          <ArrowDownLeftIcon className="w-8 h-8" />
                        ) : (
                          <ArrowUpIcon className="w-8 h-8" />
                        )}
                      </div>
                      <h2
                        className={`text-3xl font-bold font-mono mb-1 ${
                          ['CREDIT', 'TOPUP', 'REFUND'].includes(selectedTransaction.type)
                            ? 'text-[#04BFBF]'
                            : 'text-red-500'
                        }`}
                      >
                        {['CREDIT', 'TOPUP', 'REFUND'].includes(selectedTransaction.type)
                          ? '+'
                          : '-'}
                        {selectedTransaction.amount?.toLocaleString() ?? 0}{' '}
                        <span className="text-lg text-gray-400 font-normal">MMK</span>
                      </h2>
                      <span
                        className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${getStatusColor(selectedTransaction.status)}`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col justify-between items-center text-sm">
                        <span className="text-gray-500">Txn ID</span>
                        <span className="font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded copy-all select-all">
                          {(
                            selectedTransaction.transactionId ||
                            selectedTransaction._id ||
                            ''
                          ).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex flex-col justify-between items-center text-sm">
                        <span className="text-gray-500">Date & Time</span>
                        <span className="font-medium text-gray-900 text-center">
                          {new Date(selectedTransaction.createdAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </span>
                      </div>
                      <div className="flex flex-col justify-between items-center text-sm">
                        <span className="text-gray-500">Description</span>
                        <span className="font-medium text-gray-900 text-center">
                          {selectedTransaction.description ||
                            `${selectedTransaction.type} - ${selectedTransaction.referenceId || ''}`}
                        </span>
                      </div>
                      {/* Only show closing balance if available */}
                      {selectedTransaction.balanceAfter !== undefined && (
                        <div className="flex justify-between items-center text-sm pt-4 border-t border-gray-100">
                          <span className="text-gray-500">Closing Balance</span>
                          <span className="font-medium text-gray-900 font-mono">
                            {selectedTransaction.balanceAfter?.toLocaleString() ?? 0} MMK
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-1">
                  <button
                    onClick={() => setSelectedTransaction(null)}
                    className="w-full py-4 rounded-2xl font-bold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all shadow-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Top Up Tab Content */}
                {activeTab === 'top-up' && (
                  <div className="h-full bg-white p-6 md:p-8 flex flex-col shadow-inner">
                    {step === 'amount' && (
                      <div className="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-300 h-full">
                        <div className="space-y-6 flex-1">
                          <div className="text-center mt-4">
                            {error ? (
                              <p className="text-red-500 text-sm bg-red-50 px-2 rounded-xl inline-block font-medium animate-in bounce-in duration-300">
                                {error}
                              </p>
                            ) : (
                              <span className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider font-semibold">
                                Enter Amount
                              </span>
                            )}
                            <div className="relative mt-4">
                              <input
                                type="text"
                                value={amount}
                                onChange={handleAmountChange}
                                placeholder="0"
                                className="w-full text-center text-5xl font-bold py-2 bg-transparent text-gray-900 placeholder-gray-200 focus:outline-none font-mono"
                                autoFocus
                              />
                              <div className="h-1 w-24 bg-[#04BFBF] mx-auto mt-4 rounded-full opacity-20" />
                              <span className="block mt-2 text-sm text-gray-500 font-medium">
                                MMK
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3 mt-8">
                            {[10000, 50000, 100000].map((preset) => (
                              <button
                                key={preset}
                                onClick={() => {
                                  setAmount(preset.toLocaleString());
                                  setError(null);
                                }}
                                className="py-3 px-1 text-sm font-semibold bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:border-gray-300 active:bg-[#04BFBF]/10 active:text-[#04BFBF] active:border-[#04BFBF] rounded-2xl text-gray-600 transition-all font-mono"
                              >
                                +{preset.toLocaleString()}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="mt-auto">
                          <button
                            onClick={handleNextToPayment}
                            disabled={!amount}
                            className="w-full py-4 rounded-2xl font-bold text-white transition-all transform active:scale-[0.98] shadow-lg shadow-[#04BFBF]/30 disabled:opacity-50 disabled:shadow-none bg-[#04BFBF] hover:bg-[#03a0a0]"
                          >
                            Continue
                          </button>
                        </div>
                      </div>
                    )}

                    {step === 'payment' && (
                      <div className="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-300 h-full">
                        <div className="text-center mb-4">
                          <p className="text-gray-500 font-medium">Select Payment Method</p>
                          <h3 className="text-3xl font-bold text-gray-900 mb-2 font-mono">
                            {amount}{' '}
                            <span className="text-base text-gray-400 font-normal font-sans">
                              MMK
                            </span>
                          </h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <button
                            onClick={() => handlePaymentSelect('MMQR')}
                            disabled={isLoading}
                            className={`relative group p-6 border-2 rounded-3xl transition-all duration-300 flex flex-col items-center gap-4 hover:shadow-xl hover:-translate-y-1
                              ${selectedPayment === 'MMQR' ? 'border-[#04BFBF] bg-[#04BFBF]/5' : 'border-gray-100 bg-white hover:border-[#04BFBF]/30'}
                              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isLoading && selectedPayment === 'MMQR' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-3xl z-10">
                                <div className="w-8 h-8 border-4 border-[#04BFBF] border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            <div className="w-16 h-16 flex items-center justify-center filter drop-shadow-sm group-hover:drop-shadow-md transition-all bg-white rounded-2xl shadow-sm p-2">
                              <img
                                src={MMQRIcon}
                                alt="MMQR"
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <span className="font-bold text-gray-700 group-hover:text-[#04BFBF] transition-colors">
                              MMQR
                            </span>
                          </button>

                          <button
                            onClick={() => handlePaymentSelect('APLUS')}
                            disabled={isLoading}
                            className={`relative group p-6 border-2 rounded-3xl transition-all duration-300 flex flex-col items-center gap-4 hover:shadow-xl hover:-translate-y-1
                              ${selectedPayment === 'APLUS' ? 'border-[#04BFBF] bg-[#04BFBF]/5' : 'border-gray-100 bg-white hover:border-[#04BFBF]/30'}
                              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isLoading && selectedPayment === 'APLUS' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-3xl z-10">
                                <div className="w-8 h-8 border-4 border-[#04BFBF] border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            <div className="w-16 h-16 flex items-center justify-center filter drop-shadow-sm group-hover:drop-shadow-md transition-all bg-white rounded-2xl shadow-sm p-2">
                              <img
                                src={APlusIcon}
                                alt="A+ Wallet"
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <span className="font-bold text-gray-700 group-hover:text-[#04BFBF] transition-colors">
                              A+ Wallet
                            </span>
                          </button>
                        </div>
                      </div>
                    )}

                    {step === 'processing' && (
                      <div className="flex flex-col items-center justify-center py-4 animate-in fade-in duration-500 h-full w-full">
                        {showQR ? (
                          <>
                            {selectedPayment === 'MMQR' ? (
                              <div className="w-full flex flex-col items-center h-full">
                                <p className="text-gray-500 font-medium mb-4">
                                  Scan with your banking app
                                </p>

                                <div className="bg-white p-2 rounded-3xl shadow-xl border border-gray-100 mb-8 relative group transform transition-all hover:scale-105 duration-300">
                                  <div
                                    id="qr-code"
                                    className="w-[250px] h-[250px] flex items-center justify-center"
                                  ></div>
                                </div>

                                <div className="mt-auto w-full text-center">
                                  <p className="text-sm text-gray-400 mb-2">
                                    Waiting for payment confirmation...
                                  </p>
                                  <div className="mt-4 flex justify-center space-x-2">
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave"></div>
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave delay-150"></div>
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave delay-300"></div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full flex flex-col items-center h-full text-center">
                                {isMobileOrPWA ? (
                                  <>
                                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 animate-pulse">
                                      <img
                                        src={APlusIcon}
                                        alt="A+ Wallet"
                                        className="w-12 h-12 object-contain grayscale opacity-50"
                                      />
                                    </div>
                                    <p className="text-lg font-bold text-gray-900 mb-2">
                                      Opening A+ Wallet...
                                    </p>
                                    <p className="text-gray-500 text-sm max-w-[200px]">
                                      Please complete the payment in the app.
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-20 h-20 bg-[#04BFBF]/10 rounded-full flex items-center justify-center mb-6">
                                      <svg
                                        className="w-10 h-10 text-[#04BFBF]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                        />
                                      </svg>
                                    </div>
                                    <p className="text-lg font-bold text-gray-900 mb-2">
                                      Check your A+ Wallet App
                                    </p>
                                    <p className="text-gray-500 text-sm max-w-[260px] mx-auto">
                                      We've sent a payment request to your phone. Please approve it
                                      to continue.
                                    </p>
                                  </>
                                )}

                                <div className="mt-auto w-full text-center">
                                  <p className="text-sm text-gray-400 mb-2">
                                    Waiting for payment confirmation...
                                  </p>
                                  <div className="mt-4 flex justify-center space-x-2">
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave"></div>
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave delay-150"></div>
                                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-wave delay-300"></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full w-full space-y-6">
                            <div className="relative w-full max-w-[280px] bg-white rounded-3xl p-6 border border-gray-100 shadow-sm overflow-hidden">
                              {/* Shimmer Effect */}
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-12 translate-x-[-200%] animate-[shimmer-slide_1.5s_infinite]" />

                              {/* Skeleton Content */}
                              <div className="space-y-4">
                                <div className="h-6 bg-gray-100 rounded-full w-3/4 mx-auto" />
                                <div className="h-48 bg-gray-100 rounded-2xl w-full" />
                                <div className="h-10 bg-gray-100 rounded-xl w-full" />
                              </div>
                            </div>

                            <div className="space-y-2 text-center">
                              <p className="text-gray-900 font-bold text-xl animate-pulse">
                                Generating {selectedPayment === 'MMQR' ? 'QR Code' : 'Payment Link'}
                                ...
                              </p>
                              <p className="text-gray-400 text-sm">Please wait a moment</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {step === 'success' && (
                      <div className="flex flex-col items-center justify-center py-4 animate-in fade-in zoom-in duration-500 text-center h-full">
                        <div className="space-y-2 mb-8 mt-8">
                          <p className="text-gray-500 text-lg">
                            Your wallet has been credited with
                          </p>
                          <p className="text-5xl font-bold text-[#04BFBF] tracking-tight font-mono">
                            {amount}{' '}
                            <span className="text-2xl text-gray-400 font-normal font-sans">
                              MMK
                            </span>
                          </p>
                        </div>

                        <div className="w-full bg-gray-50 rounded-3xl p-6 mb-8 border border-gray-100">
                          <div className="flex justify-between items-center text-sm pb-3 border-b border-gray-200">
                            <span className="text-gray-500">Txn ID</span>
                            <span className="font-mono font-medium text-gray-900 bg-white px-2 py-1 rounded border border-gray-200">
                              {transactionId || 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm pt-3">
                            <span className="text-gray-500">Date</span>
                            <span className="font-medium text-gray-900">
                              {new Date().toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={onClose}
                          className="w-full py-4 rounded-2xl font-bold text-white bg-[#04BFBF] hover:bg-[#03a0a0] shadow-lg shadow-[#04BFBF]/25 transition-all active:scale-[0.98] mt-auto"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Withdraw Tab Content */}
                {/* {activeTab === 'withdraw' && (
                <div className="h-full bg-white p-6 md:p-8 flex flex-col shadow-inner animate-in slide-in-from-right-4 duration-300">
                  <div className="space-y-6 flex-1">
                    <div className="text-center mt-2">
                      <span className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider font-semibold">
                        Withdraw Amount
                      </span>
                      <div className="relative mt-4">
                        <input
                          type="text"
                          value={withdrawAmount}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            const formatted = raw ? parseInt(raw, 10).toLocaleString() : '';
                            setWithdrawAmount(formatted);
                          }}
                          placeholder="0"
                          className="w-full text-center text-4xl font-bold py-2 bg-transparent text-gray-900 placeholder-gray-200 focus:outline-none"
                        />
                        <div className="h-1 w-24 bg-red-400 mx-auto mt-2 rounded-full opacity-20" />
                        <span className="block mt-2 text-sm text-gray-500 font-medium">MMK</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        A+ Wallet Phone Number
                      </label>
                      <input
                        type="tel"
                        value={withdrawPhone}
                        onChange={(e) => setWithdrawPhone(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#04BFBF] focus:ring-2 focus:ring-[#04BFBF]/20 outline-none font-medium"
                        placeholder="09..."
                      />
                    </div>
                  </div>

                  <button className="w-full py-4 rounded-2xl font-bold text-white transition-all transform active:scale-[0.98] shadow-lg shadow-red-500/30 bg-red-500 hover:bg-red-600 mt-6">
                    Request Withdraw
                  </button>
                </div>
              )} */}

                {/* History Tab Content */}
                {activeTab === 'history' && (
                  <div className="p-4 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300 pb-8 min-h-full bg-gray-50 shadow-inner">
                    {/* Role Filters - Only show if user is also a merchant */}
                    {user?.merchantId && (
                      <div className="flex bg-white/50 p-1.5 rounded-2xl border border-gray-100/50 backdrop-blur-sm shadow-sm">
                        {(['all', 'user', 'merchant'] as const).map((role) => (
                          <button
                            key={role}
                            onClick={() => setSelectedRole(role)}
                            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all duration-200 capitalize
                              ${
                                selectedRole === role
                                  ? 'bg-[#04BFBF] text-white shadow-md shadow-[#04BFBF]/20 scale-[1.02]'
                                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/50'
                              }`}
                          >
                            {role}
                          </button>
                        ))}
                      </div>
                    )}

                    {isLoadingHistory ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-[#04BFBF] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-400 text-sm mt-4 font-medium">Loading history...</p>
                      </div>
                    ) : (
                      <>
                        {history.map((txn) => (
                          <div
                            key={txn.transactionId || txn._id || Math.random().toString()}
                            onClick={() => setSelectedTransaction(txn)}
                            className="flex flex-col bg-white p-4 rounded-2xl shadow-sm border border-gray-100 active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                    ['CREDIT', 'TOPUP', 'REFUND'].includes(txn.type)
                                      ? 'bg-[#04BFBF]/10 text-[#04BFBF]'
                                      : 'bg-red-50 text-red-500'
                                  }`}
                                >
                                  {getIconForType(txn.type)}
                                </div>
                                <div>
                                  <h4 className="font-bold text-gray-900 text-sm max-w-[160px] truncate">
                                    {txn.description ||
                                      `${txn.type} - ${txn.referenceId || 'No Ref'}`}
                                  </h4>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <ClockIcon className="w-3 h-3 text-gray-400" />
                                    <p className="text-xs text-gray-500 font-medium">
                                      {new Date(txn.createdAt).toLocaleString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true,
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p
                                  className={`font-bold font-mono text-base ${
                                    ['CREDIT', 'TOPUP', 'REFUND'].includes(txn.type)
                                      ? 'text-[#04BFBF]'
                                      : ['DEBIT', 'PURCHASE', 'WITHDRAW'].includes(txn.type)
                                        ? 'text-red-400'
                                        : 'text-gray-700'
                                  }`}
                                >
                                  {['CREDIT', 'TOPUP', 'REFUND'].includes(txn.type) ? '+' : '-'}
                                  {(txn.amount || 0).toLocaleString()}
                                </p>
                                <div className="mt-1">
                                  <span
                                    className={`text-[10px] tracking-wide font-bold px-2 py-0.5 rounded-md uppercase ${getStatusColor(txn.status)}`}
                                  >
                                    {txn.status}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Only show closing balance if available */}
                            {txn.balanceAfter !== undefined && (
                              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                                <span className="text-[11px] font-medium text-gray-400">
                                  Closing Balance
                                </span>
                                <span className="text-xs font-mono font-medium text-gray-600 bg-gray-50 px-2 py-0.5 rounded-md">
                                  {(txn.balanceAfter || 0).toLocaleString()}{' '}
                                  <span className="text-[10px] text-gray-400">MMK</span>
                                </span>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Empty State */}
                        {history.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 animate-in zoom-in duration-500">
                              <ClockIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium mb-6">No transactions yet</p>
                            <button
                              onClick={() => setActiveTab('top-up')}
                              className="px-6 py-2.5 rounded-xl bg-[#04BFBF]/10 text-[#04BFBF] font-bold text-sm hover:bg-[#04BFBF]/20 transition-all border border-[#04BFBF]/20"
                            >
                              Top Up Now
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Load More Button */}
                    {!isLoadingHistory && hasMore && (
                      <div className="flex justify-center pt-2 pb-4">
                        <button
                          onClick={() => fetchHistory(skip)}
                          className="px-6 py-2 rounded-xl text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* APlus Iframe Dialog */}
      {pushNotiLink && step === 'processing' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col h-[600px] max-h-[90vh]">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold text-gray-900">Complete Payment</h3>
              {/* Close only the iframe, return to processing state */}
              <button
                onClick={() => setPushNotiLink(null)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                title="Close Payment Window"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 relative bg-white">
              <iframe
                src={pushNotiLink}
                className="w-full h-full border-0"
                title="APlus Payment"
                onLoad={handleIframeLoad}
              />
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
